export type UnitSystem = "metric" | "imperial";

const HECTARES_TO_ACRES = 2.471053814671653;
const KM_TO_MILES = 0.621371;
const M_TO_FEET = 3.28084;
const METRIC_TONNES_TO_US_SHORT_TONS = 1.102311310924388;

const roundWhole = (value: number): number => Math.round(value);

export const hectaresToAcres = (hectares: number): number => roundWhole(hectares * HECTARES_TO_ACRES);

export const acresToHectares = (acres: number): number => roundWhole(acres / HECTARES_TO_ACRES);

export const kilometersToMiles = (kilometers: number): number => roundWhole(kilometers * KM_TO_MILES);

export const milesToKilometers = (miles: number): number => roundWhole(miles / KM_TO_MILES);

export const kmPerHourToMilesPerHour = (kmPerHour: number): number => roundWhole(kmPerHour * KM_TO_MILES);

export const milesPerHourToKmPerHour = (milesPerHour: number): number => roundWhole(milesPerHour / KM_TO_MILES);

export const metersToFeet = (meters: number): number => roundWhole(meters * M_TO_FEET);

export const feetToMeters = (feet: number): number => roundWhole(feet / M_TO_FEET);

export const celsiusToFahrenheit = (celsius: number): number => roundWhole((celsius * 9) / 5 + 32);

export const fahrenheitToCelsius = (fahrenheit: number): number => roundWhole(((fahrenheit - 32) * 5) / 9);

export const formatNumber = (
  value: number,
  options: Intl.NumberFormatOptions = {}
): string => {
  if (!Number.isFinite(value)) {
    return "0";
  }

  return value.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    ...options,
  });
};

export const formatSpeed = (kmPerHour: number, unitSystem: UnitSystem): string => {
  if (unitSystem === "imperial") {
    return `${formatNumber(kmPerHourToMilesPerHour(kmPerHour))} mph`;
  }
  return `${formatNumber(kmPerHour)} km/h`;
};

export const toDisplaySpeed = (metricKmPerHour: number, unitSystem: UnitSystem): number => {
  if (unitSystem === "imperial") {
    return kmPerHourToMilesPerHour(metricKmPerHour);
  }
  return metricKmPerHour;
};

export const toMetricSpeed = (displaySpeed: number, unitSystem: UnitSystem): number => {
  if (unitSystem === "imperial") {
    return milesPerHourToKmPerHour(displaySpeed);
  }
  return displaySpeed;
};

export const formatTemperature = (celsius: number, unitSystem: UnitSystem): string => {
  if (unitSystem === "imperial") {
    return `${formatNumber(celsiusToFahrenheit(celsius))} °F`;
  }
  return `${formatNumber(celsius)} °C`;
};

export const toDisplayTemperature = (metricCelsius: number, unitSystem: UnitSystem): number => {
  if (unitSystem === "imperial") {
    return celsiusToFahrenheit(metricCelsius);
  }
  return metricCelsius;
};

export const toMetricTemperature = (displayTemperature: number, unitSystem: UnitSystem): number => {
  if (unitSystem === "imperial") {
    return fahrenheitToCelsius(displayTemperature);
  }
  return displayTemperature;
};

export const formatArea = (hectares: number, unitSystem: UnitSystem): string => {
  if (unitSystem === "imperial") {
    return `${formatNumber(hectaresToAcres(hectares))} acres`;
  }

  return `${formatNumber(hectares)} ha`;
};

export const formatDistance = (kilometers: number, unitSystem: UnitSystem): string => {
  if (unitSystem === "imperial") {
    return `${formatNumber(kilometersToMiles(kilometers))} mi`;
  }

  return `${formatNumber(kilometers)} km`;
};

export const formatLength = (meters: number, unitSystem: UnitSystem): string => {
  if (unitSystem === "imperial") {
    return `${formatNumber(metersToFeet(meters))} ft`;
  }

  return `${formatNumber(meters)} m`;
};

// The Keetch-Byram Drought Index is the same quantity everywhere - the soil
// moisture deficit in the top 8 inches - but Australia quotes it in millimetres
// (0-203.2) and the US on the classic 0-800 scale (hundredths of an inch).
// The backend and the model always work in millimetres.
export const KBDI_MAX_MM = 203.2;
const KBDI_MAX_US = 800;

export const toDisplayDroughtIndex = (mm: number, unitSystem: UnitSystem): number =>
  roundWhole(unitSystem === "imperial" ? (mm * KBDI_MAX_US) / KBDI_MAX_MM : mm);

/** A value read off the display scale, back to the millimetres the backend wants. */
export const fromDisplayDroughtIndex = (shown: number, unitSystem: UnitSystem): number =>
  unitSystem === "imperial" ? (shown * KBDI_MAX_MM) / KBDI_MAX_US : shown;

/** Top of the scale on this unit system. */
export const droughtIndexScaleMax = (unitSystem: UnitSystem): number =>
  unitSystem === "imperial" ? KBDI_MAX_US : KBDI_MAX_MM;

/** Plain-language band, on the same quarter splits the US 0-800 scale uses. */
export const droughtIndexLabel = (mm: number): string => {
  const fraction = mm / KBDI_MAX_MM;
  if (fraction < 0.25) return "Low";
  if (fraction < 0.5) return "Moderate";
  if (fraction < 0.75) return "High";
  return "Extreme";
};

export const formatDroughtIndex = (mm: number, unitSystem: UnitSystem): string =>
  unitSystem === "imperial"
    ? `${toDisplayDroughtIndex(mm, unitSystem)}`
    : `${toDisplayDroughtIndex(mm, unitSystem)} mm`;

/** The scale a person on this unit system expects to see KBDI on. */
export const droughtIndexScaleLabel = (unitSystem: UnitSystem): string =>
  unitSystem === "imperial" ? "0–800" : "0–203 mm";

export const formatFuelLoad = (tonsPerHectare: number, unitSystem: UnitSystem): string => {
  if (unitSystem === "imperial") {
    return `${formatNumber(toDisplayFuelLoad(tonsPerHectare, unitSystem))} tons/acre`;
  }

  return `${formatNumber(tonsPerHectare)} t/ha`;
};

export const toDisplayFuelLoad = (metricTonsPerHectare: number, unitSystem: UnitSystem): number => {
  if (unitSystem === "imperial") {
    return roundWhole(
      (metricTonsPerHectare * METRIC_TONNES_TO_US_SHORT_TONS) / HECTARES_TO_ACRES
    );
  }
  return metricTonsPerHectare;
};

export const toMetricFuelLoad = (displayFuelLoad: number, unitSystem: UnitSystem): number => {
  if (unitSystem === "imperial") {
    return roundWhole(
      (displayFuelLoad * HECTARES_TO_ACRES) / METRIC_TONNES_TO_US_SHORT_TONS
    );
  }
  return displayFuelLoad;
};

/** t/ha to tons/acre without rounding, for values someone edits. */
export const tonsPerHectareToTonsPerAcre = (tonsPerHectare: number): number =>
  (tonsPerHectare * METRIC_TONNES_TO_US_SHORT_TONS) / HECTARES_TO_ACRES;

/** tons/acre back to t/ha without rounding. */
export const tonsPerAcreToTonsPerHectare = (tonsPerAcre: number): number =>
  (tonsPerAcre * HECTARES_TO_ACRES) / METRIC_TONNES_TO_US_SHORT_TONS;

export const formatCostPerArea = (costPerHectare: number, unitSystem: UnitSystem): string => {
  if (unitSystem === "imperial") {
    return `${formatNumber(toDisplayCostPerArea(costPerHectare, unitSystem))} /acre`;
  }
  return `${formatNumber(costPerHectare)} /ha`;
};

export const formatCostPerDistance = (costPerKilometer: number, unitSystem: UnitSystem): string => {
  if (unitSystem === "imperial") {
    return `${formatNumber(toDisplayCostPerDistance(costPerKilometer, unitSystem))} /mi`;
  }
  return `${formatNumber(costPerKilometer)} /km`;
};

export const toMetricCostPerArea = (displayCostPerArea: number, unitSystem: UnitSystem): number => {
  if (unitSystem === "imperial") {
    return roundWhole(displayCostPerArea * HECTARES_TO_ACRES);
  }
  return displayCostPerArea;
};

export const toDisplayCostPerArea = (metricCostPerArea: number, unitSystem: UnitSystem): number => {
  if (unitSystem === "imperial") {
    return roundWhole(metricCostPerArea / HECTARES_TO_ACRES);
  }
  return metricCostPerArea;
};

export const toMetricCostPerDistance = (displayCostPerDistance: number, unitSystem: UnitSystem): number => {
  if (unitSystem === "imperial") {
    return roundWhole(displayCostPerDistance * KM_TO_MILES);
  }
  return displayCostPerDistance;
};

export const toDisplayCostPerDistance = (
  metricCostPerDistance: number,
  unitSystem: UnitSystem
): number => {
  if (unitSystem === "imperial") {
    return roundWhole(metricCostPerDistance / KM_TO_MILES);
  }
  return metricCostPerDistance;
};

// Metric unit strings we can translate when they arrive as data (backend KPI
// `units`, story-map metric items) rather than as a known quantity kind.
const IMPERIAL_UNIT_CONVERSIONS: Record<
  string,
  { label: string; convert: (metricValue: number) => number }
> = {
  ha: { label: "acres", convert: hectaresToAcres },
  km: { label: "mi", convert: kilometersToMiles },
  m: { label: "ft", convert: metersToFeet },
  "t/ha": { label: "tons/acre", convert: (v) => toDisplayFuelLoad(v, "imperial") },
  "km/h": { label: "mph", convert: kmPerHourToMilesPerHour },
  kph: { label: "mph", convert: kmPerHourToMilesPerHour },
  c: { label: "°F", convert: celsiusToFahrenheit },
  "°c": { label: "°F", convert: celsiusToFahrenheit },
};

/**
 * Convert a metric-unit-labelled value into the display system, keeping the
 * number and its unit label separate (for forms that hold them in separate
 * fields). Unknown unit strings pass through untouched.
 */
export const toDisplayValueUnit = (
  value: number,
  unit: string | null | undefined,
  unitSystem: UnitSystem
): { value: number; unit: string } => {
  const trimmedUnit = (unit || "").trim();
  if (unitSystem === "imperial") {
    const conversion = IMPERIAL_UNIT_CONVERSIONS[trimmedUnit.toLowerCase()];
    if (conversion && Number.isFinite(value)) {
      return { value: conversion.convert(value), unit: conversion.label };
    }
  }
  return { value, unit: trimmedUnit };
};

/** The display-system label for a metric unit string ("ha" → "acres"). */
export const toDisplayUnitLabel = (
  unit: string | null | undefined,
  unitSystem: UnitSystem
): string => {
  const trimmedUnit = (unit || "").trim();
  if (unitSystem === "imperial") {
    const conversion = IMPERIAL_UNIT_CONVERSIONS[trimmedUnit.toLowerCase()];
    if (conversion) {
      return conversion.label;
    }
  }
  return trimmedUnit;
};

export const formatValueWithUnit = (
  value: number,
  unit: string | null | undefined,
  unitSystem: UnitSystem
): string => {
  if (!Number.isFinite(value)) {
    return "N/A";
  }

  const display = toDisplayValueUnit(value, unit, unitSystem);
  return `${formatNumber(display.value)}${display.unit ? ` ${display.unit}` : ""}`;
};

export const kwPerMeterToBtuPerFootPerSecond = (kwPerMeter: number): number => {
  // 1 kW = 1000 J/s; 1 Btu = 1055.056 J; 1 m = 3.28084 ft
  // => (1000 / 1055.056) / 3.28084
  const BTU_PER_SECOND_PER_KW = 1000 / 1055.056;
  return roundWhole((kwPerMeter * BTU_PER_SECOND_PER_KW) / M_TO_FEET);
};

export const formatFirelineIntensity = (kwPerMeter: number, unitSystem: UnitSystem): string => {
  if (unitSystem === "imperial") {
    return `${formatNumber(kwPerMeterToBtuPerFootPerSecond(kwPerMeter))} Btu/ft/s`;
  }
  return `${formatNumber(kwPerMeter)} kW/m`;
};
